import { useCallback, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  COURSE_CERTIFICATES_LIST_QUERY_KEY,
  COURSE_CERTIFICATES_QUERY_KEY,
  createCourseCertificateDetailQueryKey,
  createCourseCertificateFileQueryKey,
  deleteCourseCertificate,
  fetchCourseCertificateById,
  fetchCourseCertificateDocumentFile,
  fetchCourseCertificates,
  fetchCourseCertificatesForInjunction,
  fetchCourseCertificatesForImposition,
  fetchCourseCertificatesForReporting,
  setCourseCertificateAwaitingHumanReview,
} from "@/api/courseCertificates";
import {
  ANALYZING_REFETCH_INTERVAL_MS,
  resolveAnalyzingRefetchInterval,
} from "@/features/cle-compliance/hooks/analyzingRefetch";
import { invalidateCourseCertificateQueries } from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import { isAnalyzingStatus } from "@/features/cle-compliance/models/evidenceItem";
import type {
  CourseCertificateEvidenceListModel,
  CourseCertificateEvidenceModel,
  CourseCertificateQueryParams,
  CourseCertificateStatusResponse,
  PaginatedResponse,
} from "@/features/cle-compliance/models/courseCertificate";

export {
  COURSE_CERTIFICATES_LIST_QUERY_KEY,
  COURSE_CERTIFICATES_QUERY_KEY,
  createCourseCertificateDetailQueryKey,
  createCourseCertificateFileQueryKey,
} from "@/api/courseCertificates";

export const useCourseCertificates = (
  params: CourseCertificateQueryParams = {},
) => {
  // When the current analyzing streak began, so the poll can widen and
  // eventually give up. A ref rather than derived from the rows' updatedAt:
  // that would compare server time against client time, and clock skew would
  // silently shift the bands.
  const analyzingSinceRef = useRef<number | null>(null);

  const resolveRefetchInterval = useCallback((isAnalyzing: boolean) => {
    if (!isAnalyzing) {
      analyzingSinceRef.current = null;

      return false;
    }

    analyzingSinceRef.current ??= Date.now();

    return resolveAnalyzingRefetchInterval(
      analyzingSinceRef.current,
      Date.now(),
    );
  }, []);

  return useQuery<PaginatedResponse<CourseCertificateEvidenceListModel>>({
    queryKey: [...COURSE_CERTIFICATES_LIST_QUERY_KEY, params],
    queryFn: () => fetchCourseCertificates(params),
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) =>
      resolveRefetchInterval(
        query.state.data?.items.some((certificate) =>
          isAnalyzingStatus(certificate.status),
        ) ?? false,
      ),
  });
};

export const useCourseCertificatesForReporting = (periodId: string) =>
  useQuery<CourseCertificateEvidenceListModel[]>({
    queryKey: [...COURSE_CERTIFICATES_QUERY_KEY, "for-reporting", periodId],
    queryFn: () => fetchCourseCertificatesForReporting(periodId),
    staleTime: 5 * 60 * 1000,
    // Keep the reportable lessons up to date while a certificate is still
    // being analyzed, so the "available lessons" figure in an open
    // indberetning reflects certificates that finish analyzing while the
    // user is viewing it (not only once staleTime lapses or on navigation).
    refetchInterval: (query) =>
      query.state.data?.some((certificate) =>
        isAnalyzingStatus(certificate.status),
      )
        ? ANALYZING_REFETCH_INTERVAL_MS
        : false,
  });

export const useCourseCertificatesForInjunction = (
  injunctionId: string,
  enabled = true,
) =>
  useQuery<CourseCertificateEvidenceListModel[]>({
    enabled: enabled && injunctionId.length > 0,
    queryKey: [
      ...COURSE_CERTIFICATES_QUERY_KEY,
      "for-injunction",
      injunctionId,
    ],
    queryFn: () => fetchCourseCertificatesForInjunction(injunctionId),
    staleTime: 5 * 60 * 1000,
  });

export const useCourseCertificatesForImposition = (
  impositionId: string,
  enabled = true,
) =>
  useQuery<CourseCertificateEvidenceListModel[]>({
    enabled: enabled && impositionId.length > 0,
    queryKey: [
      ...COURSE_CERTIFICATES_QUERY_KEY,
      "for-imposition",
      impositionId,
    ],
    queryFn: () => fetchCourseCertificatesForImposition(impositionId),
    staleTime: 5 * 60 * 1000,
  });

export const useCourseCertificateDocument = (id: string) =>
  useQuery<CourseCertificateEvidenceModel>({
    enabled: id.length > 0,
    queryKey: createCourseCertificateDetailQueryKey(id),
    queryFn: () => fetchCourseCertificateById(id),
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data && isAnalyzingStatus(query.state.data.status)
        ? ANALYZING_REFETCH_INTERVAL_MS
        : false,
  });

export const useCourseCertificateDocumentFile = (id?: string) =>
  useQuery<Blob>({
    enabled: !!id,
    queryKey: createCourseCertificateFileQueryKey(id ?? ""),
    queryFn: () => fetchCourseCertificateDocumentFile(id!),
    staleTime: 5 * 60 * 1000,
  });

export const useDeleteCourseCertificate = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteCourseCertificate,
    onSuccess: (_data, id) => {
      invalidateCourseCertificateQueries(queryClient, id);
    },
  });
};

export const useSetCourseCertificateAwaitingHumanReview = () => {
  const queryClient = useQueryClient();

  return useMutation<CourseCertificateStatusResponse, Error, string>({
    mutationFn: setCourseCertificateAwaitingHumanReview,
    onSuccess: () => {
      invalidateCourseCertificateQueries(queryClient);
    },
  });
};
